import { Pipe, PipeTransform, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked';
import { DYNAMIC_COMPONENT } from '../directives/dynamic-component-renderer.directive';

@Pipe({
  standalone: true,
  name: 'formatText',
})
export class FormatTextPipe implements PipeTransform {
  showOpenButton: boolean = true;
  constructor(private sanitizer: DomSanitizer) {
    marked.setOptions({
      gfm: true,
      breaks: true,
      renderer: this.createCustomRenderer()
    });
  }

  /**
   * Transform markdown text to formatted HTML with source citations
   * @param value - The markdown text to transform
   * @param showOpenButton - Whether to show open button for tables
   * @returns SafeHtml with formatted content including inline source citations
   */
  transform(value: string, showOpenButton: boolean = true): SafeHtml {
    if (!value) return '';
    this.showOpenButton = showOpenButton;

    // CRITICAL FIX: Convert escaped newlines to actual newlines
    // The JSON contains literal \n strings that need to be converted to actual newline characters
    let processedValue = value
      .replace(/\\n/g, '\n')      // Convert \n to actual newlines
      .replace(/\\r/g, '\r')      // Convert \r to actual carriage returns
      .replace(/\\t/g, '\t')      // Convert \t to actual tabs
      .replace(/\\"/g, '"')       // Convert \" to actual quotes
      .replace(/\\\\/g, '\\');    // Convert \\ to actual backslash

    // Convert LaTeX to plain text
    processedValue = processedValue.replace(/\$(.*?)\$/g, (_, content) => {
      return this.convertLatexToPlainText(content);
    });

    // First, generate HTML from markdown
    let html = marked(processedValue) as string;

    // Then process source tags on the complete HTML output
    // This ensures multi-line source tags are captured correctly
    html = this.processSourceTags(html);

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  /**
   * Process source tags in the complete HTML output
   * This method handles multi-line source citations by operating on the full HTML
   * after Marked has completed processing, ensuring tags that span multiple lines are captured
   * @param html - The complete HTML string generated by Marked
   * @returns HTML string with source tags converted to inline tooltips
   */
  private processSourceTags(html: string): string {
    // Match source tags that may span multiple lines (now with <br> tags)
    // Pattern: &lt;N&gt;content&lt;/N&gt; with optional [Page X] or [Pages X] (supports commas, hyphens, en-dash, em-dash, spaces)
    return html.replace(
      /&lt;(\d+)&gt;(?:\[(.+?)\]:)?([\s\S]*?)&lt;\/\1&gt;(?:\s*\[Pages?\s+([\d\s,.–—-]+)\])?/g,
      (match, group, label, content, pageNum) => {
        const sourceNum = group.trim();


        const cleanedContent = content
          .replace(/\\n/g, ' ')
          .replace(/\\r/g, ' ');

        
        const tooltipData = {
          label: (label || '').trim(),
          sourceText: cleanedContent,
          pageNum: (pageNum || '').trim()
        };

        // Create valid JSON string
        const jsonString = JSON.stringify(tooltipData);

        // Escape for HTML attribute (double quotes and apostrophes)
        const tooltipContent = jsonString
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');

        return ` 
          <span class="relative group">
            <span class="dynamic-component"
            data-component="${DYNAMIC_COMPONENT.TOOLTIP}"
            data-tooltip-content="${tooltipContent}"
            data-tooltip-number="${sourceNum}"
            ></span>
          </span>
        `;
      }
    );
  }

  private createCustomRenderer() {
    const renderer = new marked.Renderer();

    // Customize link rendering for security
    renderer.link = (href: string, title: string, text: string) => {
      const sanitizedHref = this.sanitizer.sanitize(SecurityContext.URL, href) || '';
      return `<a href="${sanitizedHref}" ${title ? `title="${title}"` : ''} target="_blank" rel="noopener noreferrer">${text}</a>`;
    };

    renderer.table = (header: string, body: string) => {
      return `
              <div class="overflow-x-auto text-xs dynamic-component" data-component="${DYNAMIC_COMPONENT.TABLE_MODEL}" data-show-open-button="${this.showOpenButton}">
                <div class="flex justify-end dynamic-table-model" >
                </div>
                <table class="min-w-full table-auto border-collapse border border-gray-200 mb-4">
                  <thead class="bg-gray-100">
                    ${header.replace(/<th>/g, '<th class="px-4 py-2 border border-gray-300 text-left text-xs font-medium text-gray-700">')
          .replace(/<\/th>/g, '</th>')}
                  </thead>
                  <tbody class="bg-white text-xs">
                    ${body.replace(/<td>/g, '<td class="px-4 py-2 border border-gray-300 text-xs text-gray-600">')
          .replace(/<\/td>/g, '</td>')
          .replace(/<tr>/g, '<tr class="hover:bg-gray-50">')
          .replace(/<\/tr>/g, '</tr>')}
                  </tbody>
                </table>
              </div>
            `;
    };

    renderer.code = (code: string, language: string) => {
      try {
        if (language === "json") {
          // First process functions in the raw string
          const processedJson = this.escapeJson(code);
          return `<span class="dynamic-component" data-component="${DYNAMIC_COMPONENT.GRAPH_MODEL}" data-json-content='${processedJson}'></span>`;
        }
        return ``
      }
      catch (error) {
        return ``;
      }
    }

    // Customize text rendering for inline formatting
    renderer.text = (text: string) => {
      let formattedText = text;

      formattedText = formattedText.replace(/\n/g, '<br>');

      formattedText = formattedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

      formattedText = formattedText.replace(/\*(.*?)\*/g, '<em>$1</em>');

      // Jira ticket ID
      formattedText = formattedText.replace(
        /\b(ARN-\d+)\b/g,
        '<a href="https://inncircles.atlassian.net/browse/$1" target="_blank">$1</a>'
      );

      formattedText = formattedText.replace(
        /\[\s*([^\]]+?)\s*\]\(\s*(https?:\/\/[^\s]+?)\s*\)/g,
        (match, p1, p2) => `<a href="${p2.trim()}" target="_blank">${p1.trim()}</a>`
      );

      return formattedText;
    };
    return renderer;
  }

  private serializeFunctions(input: string): string {
    // Handle function definitions in the string, including newlines and special characters
    return input.replace(
      /:\s*function\s*\(([^)]*)\)\s*{([^}]*)}/g,
      (match, params, body) => {
        // Clean up the function body: remove newlines and escape quotes
        const cleanBody = body
          .replace(/\n/g, ' ')
          .replace(/\r/g, '')
          .replace(/\t/g, ' ')
          .trim();

        // Create the function string with proper escaping
        const functionStr = `##FUNCTION_START##function(${params.trim()}){${cleanBody}}##FUNCTION_END##`;

        // Escape for JSON string context
        return `: "${functionStr.replace(/"/g, '\\"')}"`;
      }
    );
  }

  private escapeJson(jsonString: string): string {
    if (!jsonString) return '';

    try {
      // Clean up the input string first
      const cleanInput = jsonString
        .replace(/\n/g, ' ')
        .replace(/\r/g, '')
        .replace(/\t/g, ' ')
        .trim();

      // Handle functions in the cleaned string
      const preprocessed = this.serializeFunctions(cleanInput);

      // Double check the JSON is valid
      JSON.parse(preprocessed);

      // Escape single quotes for HTML attribute context
      return preprocessed.replace(/'/g, '&apos;');
    } catch (e) {
      console.warn('JSON processing error:', e);
      return '';  // Return empty string on error to prevent invalid JSON
    }
  }

  /**
   * Escape HTML content for safe JSON embedding
   * @param unsafe - The HTML string to escape
   * @returns Escaped HTML string safe for JSON
   */
  private escapeHtml(unsafe: string): string {
    if (!unsafe)
      return '';

    return unsafe
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
  }

  /**
   * Escape content specifically for JSON string embedding
   * This handles quotes and other characters that break JSON
   * @param content - The content to escape for JSON
   * @returns JSON-safe escaped string
   */
  private escapeForJson(content: string): string {
    if (!content) return '';

    return content
      .replace(/\\/g, '\\\\')  // Escape backslashes first
      .replace(/"/g, '\\"')    // Escape double quotes
      .replace(/\n/g, '\\n')   // Escape newlines
      .replace(/\r/g, '\\r')   // Escape carriage returns
      .replace(/\t/g, '\\t');  // Escape tabs
  }

  private convertLatexToPlainText(text: string): string {
    if (!text) return '';

    return text

      .replace(/(\d+)\s*\^\{\s*\\circ\s*\}\s*\\mathrm\{F\}/gi, '$1°F')
      .replace(/\\circ\s*\\mathrm\{F\}/gi, '°F')
      .replace(/\\mathrm\{F\}/gi, '°F')
      .replace(/\\circ/gi, '°')

      .replace(/\\mathrm\{C\}/gi, '°C')

      .replace(/\^\{\\prime\\prime\}/gi, '”')
      .replace(/\\prime\\prime/gi, '”')

      .replace(/\^\{\\prime\}/gi, '’')
      .replace(/\\prime/gi, '’')

      .replace(/"{2}/g, '”')

      .replace(/\\frac\{(\d+)\}\{(\d+)\}/g, '$1/$2')

      .replace(/(\d+)\s*\^\{\s*(\d+)\s*\/\s*(\d+)\s*\}/g, '$1 $2/$3')


      .replace(/\^\{([^}]+)\}/g, '$1')
      .replace(/_\{([^}]+)\}/g, '$1')


      .replace(/\\times/gi, '×')
      .replace(/\\pm/gi, '±')
      .replace(/\\leq/gi, '≤')
      .replace(/\\geq/gi, '≥')
      .replace(/\\neq/gi, '≠')
      .replace(/\\approx/gi, '≈')


      .replace(/\\mathrm\{psi\}/gi, 'psi')
      .replace(/\\mathrm\{psig\}/gi, 'psig')
      .replace(/\\mathrm\{lb\}/gi, 'lb')
      .replace(/\\mathrm\{in\}/gi, 'in')
      .replace(/\\mathrm\{ft\}/gi, 'ft')
      .replace(/\\mathrm\{mm\}/gi, 'mm')
      .replace(/\\mathrm\{cm\}/gi, 'cm')


      // remove any remaining \command
      .replace(/\\[a-zA-Z]+/g, '')

      // remove braces
      .replace(/[{}]/g, '')


      .replace(/\s+/g, ' ')
      .trim();
  }

}
